import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, CheckCircle2, Phone } from "lucide-react";

interface PhoneVerificationProps {
  phone: string;
  onPhoneChange: (phone: string) => void;
  onVerified: (verified: boolean) => void;
  isVerified: boolean;
  accentColor?: string;
}

export function PhoneVerification({
  phone,
  onPhoneChange,
  onVerified,
  isVerified,
  accentColor = "blue",
}: PhoneVerificationProps) {
  const [verificationCode, setVerificationCode] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const sendVerificationCode = async () => {
    if (!phone.trim()) {
      setError("Please enter a phone number");
      return;
    }

    setIsSending(true);
    setError("");
    setMessage("");

    try {
      const response = await fetch("/api/send-verification", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.details || data.error || "Failed to send code");
      }

      setCodeSent(true);
      setMessage("Verification code sent! Check your phone.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to send verification code");
    } finally {
      setIsSending(false);
    }
  };

  const checkVerificationCode = async () => {
    if (!verificationCode.trim()) {
      setError("Please enter the verification code");
      return;
    }

    setIsVerifying(true);
    setError("");
    setMessage("");

    try {
      const response = await fetch("/api/check-verification", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone, code: verificationCode }),
      });

      const data = await response.json();

      if (!response.ok || !data.verified) {
        throw new Error(data.message || "Invalid verification code");
      }

      onVerified(true);
      setMessage("Phone number verified!");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Verification failed");
    } finally {
      setIsVerifying(false);
    }
  };

  const resetVerification = () => {
    setCodeSent(false);
    setVerificationCode("");
    onVerified(false);
    setError("");
    setMessage("");
  };

  const buttonColorClass = accentColor === "emerald" 
    ? "bg-emerald-600 hover:bg-emerald-700" 
    : "bg-blue-600 hover:bg-blue-700";

  if (isVerified) {
    return (
      <div className="space-y-2">
        <Label htmlFor="phone">Phone Number</Label>
        <div className="flex items-center gap-2">
          <Input
            id="phone"
            type="tel"
            value={phone}
            disabled
            className="flex-1"
          />
          <div className="flex items-center gap-1 text-green-600">
            <CheckCircle2 className="h-5 w-5" />
            <span className="text-sm font-medium">Verified</span>
          </div>
        </div>
        <button
          type="button"
          onClick={resetVerification}
          className={`text-sm text-${accentColor}-600 hover:underline`}
        >
          Change phone number
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4 rounded-md border p-4">
      <div className="flex items-center gap-2 text-sm font-medium">
        <Phone className="h-4 w-4" />
        Phone Verification
      </div>

      <div className="space-y-2">
        <Label htmlFor="phone">Phone Number</Label>
        <div className="flex gap-2">
          <Input
            id="phone"
            type="tel"
            value={phone}
            onChange={(e) => {
              onPhoneChange(e.target.value);
              if (codeSent) {
                resetVerification();
              }
            }}
            placeholder="(555) 123-4567"
            disabled={codeSent}
            className="flex-1"
          />
          {!codeSent && (
            <Button
              type="button"
              onClick={sendVerificationCode}
              disabled={isSending || !phone.trim()}
              className={buttonColorClass}
            >
              {isSending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Send Code"
              )}
            </Button>
          )}
        </div>
      </div>

      {codeSent && (
        <div className="space-y-2">
          <Label htmlFor="verificationCode">Verification Code</Label>
          <div className="flex gap-2">
            <Input
              id="verificationCode"
              type="text"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="Enter 6-digit code"
              maxLength={6}
              className="flex-1"
            />
            <Button
              type="button"
              onClick={checkVerificationCode}
              disabled={isVerifying || verificationCode.length < 4}
              className={buttonColorClass}
            >
              {isVerifying ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Verify"
              )}
            </Button>
          </div>
          <button
            type="button"
            onClick={sendVerificationCode}
            disabled={isSending}
            className={`text-sm text-${accentColor}-600 hover:underline`}
          >
            {isSending ? "Sending..." : "Resend code"}
          </button>
        </div>
      )}

      {error && (
        <p className="text-sm text-destructive">{error}</p>
      )}
      {message && !error && (
        <p className="text-sm text-green-600">{message}</p>
      )}
    </div>
  );
}



